import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { ROUTES } from 'app/javascript/constants/routes';
import type { StoreProduct } from 'app/javascript/data/storeCatalogs';

interface ProductCardProps {
  product: StoreProduct;
  academyName: string;
  companySlug: string;
}

// A product tile in the store grid. The whole card links to the product detail
// page, where the size is chosen and the WhatsApp purchase happens.
export default function ProductCard({ product, academyName, companySlug }: ProductCardProps) {
  const [imageBroken, setImageBroken] = useState(false);

  return (
    <Link
      to={ROUTES.COMPANY_STORE_PRODUCT}
      params={{ slug: companySlug, productId: product.id }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-800/50 backdrop-blur-sm transition-colors hover:border-yellow-400/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/50"
    >
      {imageBroken ? (
        <div className="flex aspect-square w-full items-center justify-center bg-slate-900 text-slate-600">
          <MessageCircle className="size-10" aria-hidden />
        </div>
      ) : (
        <div className="relative aspect-square w-full overflow-hidden bg-slate-900">
          <img
            src={product.image}
            alt={`${product.name} — ${academyName}`}
            loading="lazy"
            onError={() => setImageBroken(true)}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </div>
      )}

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="min-w-0">
          <h3 className="font-bebas text-xl uppercase leading-tight tracking-wide text-foreground transition-colors group-hover:text-yellow-400">
            {product.name}
          </h3>
          {product.variant && <p className="mt-0.5 text-xs text-slate-400">{product.variant}</p>}
        </div>

        <span className="mt-auto inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-yellow-400">
          Ver produto
          <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
        </span>
      </div>
    </Link>
  );
}
